import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import PageHero from "@/components/PageHero";
import { blogApi } from "@/lib/api";
import { useSeo, JsonLd } from "@/lib/seo";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Blog() {
  useSeo({
    title: "Blog DJ événementiel & conseils mariage | My Animation",
    description:
      "Conseils mariage, anniversaire, entreprise. Astuces playlist, sonorisation, photobooth. Le blog de My Animation, DJ événementiel dans les Hauts-de-France.",
    path: "/blog",
    keywords: "blog DJ mariage, conseils mariage, playlist mariage, photobooth conseils, DJ Hauts-de-France",
  });

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogApi.list({ limit: 50 })
      .then((data) => setPosts(Array.isArray(data) ? data : []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Le Journal"
        title={<>Conseils & inspirations <span className="italic gold-text">événementiels.</span></>}
        subtitle="Playlists qui font danser, astuces de planning, retours d'expérience terrain : tout ce qu'on aurait aimé savoir avant son événement."
        img="https://images.pexels.com/photos/1481317/pexels-photo-1481317.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <JsonLd
        id="jsonld-blog-list"
        data={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Blog My Animation",
          url: "https://myanimation.fr/blog",
          publisher: {
            "@type": "Organization",
            name: "My Animation",
            url: "https://myanimation.fr",
          },
        }}
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        {loading ? (
          <p className="text-white/55 text-center italic">Chargement…</p>
        ) : posts.length === 0 ? (
          <div className="card-luxe p-12 md:p-16 text-center">
            <p className="text-white/70">Les premiers articles arrivent très bientôt. Restez à l'écoute !</p>
            <Link to="/contact" className="btn-ghost-gold mt-8 inline-flex" data-testid="blog-empty-cta">
              Demander un devis
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
            {posts.map((p) => (
              <Link
                key={p.id || p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group card-luxe overflow-hidden flex flex-col hover:border-[#DCB457]/45 transition"
              >
                {p.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden bg-black/50">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-7 flex-1 flex flex-col">
                  {p.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {p.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-[0.16em] text-[#DCB457] border border-[#DCB457]/30 px-2 py-1">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <h2 className="font-serif-luxe text-2xl leading-snug group-hover:text-[#DCB457] transition">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="text-sm text-white/65 mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                  )}
                  <div className="flex items-center justify-between mt-auto pt-6 text-[11px] uppercase tracking-[0.18em] text-white/50">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} strokeWidth={1.4} /> {formatDate(p.published_at)}
                    </span>
                    {p.reading_time_min && (
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={12} strokeWidth={1.4} /> {p.reading_time_min} min
                      </span>
                    )}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-[#DCB457]">
                    Lire l'article <ArrowRight size={13} strokeWidth={1.4} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
